// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 11-observability.md#per-app-metrics-persistence
// CPU / memory history charts for the app-detail Monitoring tab. A 1h/24h/7d range
// selector drives two series reads (api.metrics.getSeries) — the REST mirror of
// the MCP getMetricsSeries tool — rendered as downsampled sparklines with an
// avg/peak summary. Points come from the persisted 1-minute samples bucketed in
// Postgres; an empty bucket is a collection gap, so a sparse series still draws.
import * as React from 'react';

import { MetricSparkline } from '@/components/greenlight/MetricSparkline';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import type { AppMetricSeriesMetric, AppMetricSeriesStep } from '@greenlight/shared';

import { AdEyebrow } from './tab-primitives';

type RangeKey = '1h' | '24h' | '7d';

interface RangeDef {
  label: string;
  windowMs: number;
  step: AppMetricSeriesStep;
}

// Each range pairs a step that keeps the bucket count modest: 1h→1m (60),
// 24h→5m (288), 7d→1h (168) — all well under the service's bucket cap.
const RANGES: Record<RangeKey, RangeDef> = {
  '1h': { label: '1h', windowMs: 3_600_000, step: '1m' },
  '24h': { label: '24h', windowMs: 86_400_000, step: '5m' },
  '7d': { label: '7d', windowMs: 7 * 86_400_000, step: '1h' },
};
const RANGE_ORDER: readonly RangeKey[] = ['1h', '24h', '7d'];

interface ChartSpec {
  metric: AppMetricSeriesMetric;
  label: string;
  suffix: string;
  digits: number;
  /** Threshold coloring — CPU uses the Meter/MetricSparkline convention {warn:70,crit:90}%.
   *  Memory has no target so stays neutral (primary tone). */
  thresholds?: { warn: number; crit: number };
}

const CHARTS: readonly ChartSpec[] = [
  // CPU area chart colored by value band — follows Meter/MetricSparkline threshold convention.
  { metric: 'cpu', label: 'CPU', suffix: '%', digits: 1, thresholds: { warn: 70, crit: 90 } },
  // Memory stays primary/neutral — no meaningful threshold benchmark at MVP.
  { metric: 'memory', label: 'Memory', suffix: ' MB', digits: 0 },
];

function fmtStat(v: number | null, suffix: string, digits: number): string {
  if (v === null) return '—';
  const n = digits === 0 ? Math.round(v) : Number(v.toFixed(digits));
  return `${n}${suffix}`;
}

interface SeriesBounds {
  from: string;
  to: string;
  step: AppMetricSeriesStep;
}

// The selected range survives the tab unmounting (switching tabs and back must
// not reset 7d → 24h), but deliberately not a reload — session-scoped, not a
// durable preference.
function readStoredRange(): RangeKey {
  try {
    const v = sessionStorage.getItem('gl-metrics-range');
    return v !== null && v in RANGES ? (v as RangeKey) : '24h';
  } catch {
    return '24h';
  }
}

export function AppMetricsHistory({ appId }: { appId: string }): React.ReactElement {
  const [range, setRangeState] = React.useState<RangeKey>(readStoredRange);
  const setRange = (r: RangeKey): void => {
    setRangeState(r);
    try {
      sessionStorage.setItem('gl-metrics-range', r);
    } catch {
      // Storage unavailable (private mode/quota) — the in-memory state still works.
    }
  };

  // Advance the window anchor every 60 s so the chart stays "last N" rather
  // than anchoring to the moment the range was first selected. 60 s matches
  // the 1-minute collection granularity — finer would change the query key
  // faster than new data arrives. The bounds are still memoized (not recomputed
  // on every render), so switching tabs or other re-renders don't cause churn.
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const bounds = React.useMemo<SeriesBounds>(() => {
    const def = RANGES[range];
    return {
      from: new Date(nowMs - def.windowMs).toISOString(),
      to: new Date(nowMs).toISOString(),
      step: def.step,
    };
  }, [range, nowMs]);

  return (
    <div className="space-y-3" data-testid="metrics-history">
      {/* History heading + segmented range control */}
      <div className="mt-7 mb-3 flex items-center justify-between">
        <p className="text-[13.5px] font-bold text-foreground">History</p>
        {/* Segmented control: track + pill buttons */}
        <div
          className="inline-flex items-center gap-1 rounded-gl bg-muted/60 p-0.5"
          role="group"
          aria-label="History range"
        >
          {RANGE_ORDER.map((key) => {
            const active = key === range;
            return (
              <button
                key={key}
                type="button"
                data-rm-range={key}
                aria-pressed={active}
                onClick={() => setRange(key)}
                className={cn(
                  'h-7 rounded-gl px-2.5 text-[12px] transition-colors duration-[120ms] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  active
                    ? 'bg-primary font-semibold text-primary-foreground'
                    : 'font-medium text-muted-foreground hover:text-foreground',
                )}
              >
                {RANGES[key].label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Two-up chart grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {CHARTS.map((spec) => (
          <MetricChart key={spec.metric} appId={appId} bounds={bounds} spec={spec} />
        ))}
      </div>
    </div>
  );
}

function MetricChart({
  appId,
  bounds,
  spec,
}: {
  appId: string;
  bounds: SeriesBounds;
  spec: ChartSpec;
}): React.ReactElement {
  // Key by the actual from/to so the cache entry advances each time the 60 s
  // tick produces a new window. bounds only changes on tick or range switch
  // (not every render), so this doesn't cause query churn.
  const key = ['metrics-series', appId, spec.metric, bounds.from, bounds.to];
  const { data, isLoading, isError } = api.metrics.getSeries.useQuery(
    key,
    {
      params: { id: appId },
      query: { metric: spec.metric, from: bounds.from, to: bounds.to, step: bounds.step },
    },
    // The window anchor only advances every 60 s, so within that tick the same
    // key can't yield newer data — serve the cache on tab revisits instead of
    // refetching (the Monitoring tab unmounts on switch).
    { queryKey: key, staleTime: 60_000 },
  );

  let chart: React.ReactNode;
  if (isLoading) {
    chart = <Skeleton className="h-10 w-full" />;
  } else if (isError || !data || data.status !== 200) {
    chart = (
      <Alert variant="destructive" className="py-2" data-testid={`history-error-${spec.metric}`}>
        <AlertDescription>Couldn't load {spec.label} history.</AlertDescription>
      </Alert>
    );
  } else {
    const body = data.body;
    // Drop empty buckets — a sparse line over the present samples is more useful
    // than a flat-lined gap. Fewer than two points can't form a line.
    const values = body.points.map((p) => p.value).filter((v): v is number => v !== null);
    if (values.length < 2 || body.summary === null) {
      chart = (
        <p
          className="py-3 text-xs text-muted-foreground"
          data-testid={`history-empty-${spec.metric}`}
        >
          Not enough data in this range yet.
        </p>
      );
    } else {
      // Y-axis labels must match the plotted line (bucket averages), not the
      // raw-sample summary — a raw spike can exceed any bucket average so
      // summary.max could be above the line's actual peak.
      const plotMin = values.reduce((m, v) => Math.min(m, v), values[0] ?? 0);
      const plotMax = values.reduce((m, v) => Math.max(m, v), values[0] ?? 0);
      chart = (
        <>
          <div className="flex gap-2.5">
            {/* Y-axis labels: max at top, min at bottom */}
            <div className="flex shrink-0 flex-col justify-between py-px text-right">
              <span className="font-mono text-[10px] leading-none text-muted-foreground">
                {fmtStat(plotMax, spec.suffix, spec.digits)}
              </span>
              <span className="font-mono text-[10px] leading-none text-muted-foreground">
                {fmtStat(plotMin, spec.suffix, spec.digits)}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <MetricSparkline
                data={values}
                thresholds={spec.thresholds}
                ariaLabel={`${spec.label} history`}
              />
            </div>
          </div>
          {/* avg/peak footer */}
          <p className="mt-2 font-mono text-[11.5px] text-muted-foreground">
            avg {fmtStat(body.summary.avg, spec.suffix, spec.digits)} · peak{' '}
            {fmtStat(body.summary.max, spec.suffix, spec.digits)}
          </p>
        </>
      );
    }
  }

  return (
    <div
      className="rounded-gl-icon border border-border p-3.5"
      data-testid={`history-chart-${spec.metric}`}
    >
      <AdEyebrow className="mb-2">{spec.label}</AdEyebrow>
      <div>{chart}</div>
    </div>
  );
}
